/**
 * Leviathan Web - Audit Logs Page
 * Quasar Prime LLC
 *
 * View system activity and change history with filtering
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  History,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  Eye,
  X,
  BarChart3,
  User,
  FileText,
  Edit,
  Trash2,
  LogIn,
  LogOut,
  Mail,
  Download,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface AuditLog {
  id: string;
  tenantId: string | null;
  userId: string | null;
  userEmail: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  timestamp: string;
  user?: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
}

interface Stats {
  period: { days: number; startDate: string };
  totalLogs: number;
  byAction: Array<{ action: string; count: number }>;
  byEntity: Array<{ entity: string; count: number }>;
  recentLogs: AuditLog[];
}

const ACTION_CONFIG: Record<string, { icon: typeof Edit; color: string }> = {
  CREATE: { icon: FileText, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  UPDATE: { icon: Edit, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  DELETE: { icon: Trash2, color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  LOGIN: { icon: LogIn, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  LOGOUT: { icon: LogOut, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  STATUS_CHANGE: { icon: RefreshCw, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  EMAIL: { icon: Mail, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  EXPORT: { icon: Download, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  VIEW: { icon: Eye, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};


export default function AuditLogs() {
  const { accessToken } = useAuth();
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [filters, setFilters] = useState({
    entity: '',
    action: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Fetch logs
  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['audit-logs', page, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '50',
      });
      if (filters.entity) params.append('entity', filters.entity);
      if (filters.action) params.append('action', filters.action);
      if (filters.search) params.append('search', filters.search);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`${API_URL}/api/audit?${params}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      return response.json();
    },
    enabled: !!accessToken,
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/audit/stats?days=30`, { headers });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json() as Promise<Stats>;
    },
    enabled: !!accessToken && showStats,
  });

  // Fetch filter options
  const { data: entityTypes } = useQuery({
    queryKey: ['audit-entity-types'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/audit/entity-types`, { headers });
      if (!response.ok) throw new Error('Failed to fetch entity types');
      const data = await response.json();
      return data.entityTypes as string[];
    },
    enabled: !!accessToken,
  });

  const { data: actions } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/audit/actions`, { headers });
      if (!response.ok) throw new Error('Failed to fetch actions');
      const data = await response.json();
      return data.actions as string[];
    },
    enabled: !!accessToken,
  });

  const logs = logsData?.data || [];
  const meta = logsData?.meta;

  const clearFilters = () => {
    setFilters({
      entity: '',
      action: '',
      search: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const getActionConfig = (action: string) => {
    return ACTION_CONFIG[action] || { icon: FileText, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-amber-500/20 border border-amber-500/30">
            <History className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Audit Logs</h1>
            <p className="text-sm text-slate-400">
              System activity and change history
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowStats(!showStats)}
            className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
              showStats
                ? 'bg-amber-500/20 border-amber-500/30 text-amber-500'
                : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Stats
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 text-slate-300 hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 p-4">
            <div className="text-slate-400 text-xs uppercase tracking-wider mb-2">Total Events (30d)</div>
            <div className="text-2xl font-bold text-amber-500">{stats.totalLogs.toLocaleString()}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 p-4">
            <div className="text-slate-400 text-xs uppercase tracking-wider mb-2">Top Actions</div>
            <div className="space-y-1">
              {stats.byAction.slice(0, 3).map((a) => (
                <div key={a.action} className="flex justify-between text-sm">
                  <span className="text-slate-300">{a.action}</span>
                  <span className="text-amber-500">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 p-4">
            <div className="text-slate-400 text-xs uppercase tracking-wider mb-2">Top Entities</div>
            <div className="space-y-1">
              {stats.byEntity.slice(0, 3).map((e) => (
                <div key={e.entity} className="flex justify-between text-sm">
                  <span className="text-slate-300">{e.entity}</span>
                  <span className="text-amber-500">{e.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 p-4">
            <div className="text-slate-400 text-xs uppercase tracking-wider mb-2">Recent Activity</div>
            <div className="text-2xl font-bold text-amber-500">{stats.recentLogs.length}</div>
            {stats.recentLogs.length > 0 && (
              <div className="text-xs text-slate-500 mt-1">Most recent logs</div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-slate-800/50 border border-slate-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, search: e.target.value }));
                  setPage(1);
                }}
                placeholder="Search descriptions..."
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          {/* Entity Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filters.entity}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, entity: e.target.value }));
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">All Entities</option>
              {entityTypes?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Action Filter */}
          <select
            value={filters.action}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, action: e.target.value }));
              setPage(1);
            }}
            className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
          >
            <option value="">All Actions</option>
            {actions?.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            />
            <span className="text-slate-500">to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            />
          </div>

          <button
            onClick={clearFilters}
            className="text-slate-400 hover:text-amber-500 text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-amber-500 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && logs.length === 0 && (
        <div className="bg-slate-800/50 border border-slate-700 p-12 text-center">
          <History className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Audit Logs Found</h3>
          <p className="text-slate-400">
            {Object.values(filters).some((v) => v)
              ? 'No logs match your current filters.'
              : 'No activity has been logged yet.'}
          </p>
        </div>
      )}

      {/* Logs Table */}
      {!isLoading && logs.length > 0 && (
        <>
          <div className="bg-slate-800/50 border border-slate-700 overflow-hidden">
            <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-700 text-slate-400 text-xs">
              Showing {logs.length} of {meta?.total || 0} events
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Changes
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-16">
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {logs.map((log: AuditLog) => {
                  const actionConfig = getActionConfig(log.action);
                  const ActionIcon = actionConfig.icon;
                  const userName = log.user
                    ? `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() || log.user.email
                    : 'System';
                  return (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className="hover:bg-slate-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                        {new Date(log.timestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-500" />
                          <div>
                            <div className="text-white text-sm">{userName}</div>
                            {log.userEmail && (
                              <div className="text-slate-500 text-xs">{log.userEmail}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border ${actionConfig.color}`}>
                          <ActionIcon className="h-3.5 w-3.5" />
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300 text-sm">{log.entity}</div>
                        {log.entityId && (
                          <div className="text-slate-500 text-xs truncate max-w-[150px]">
                            ID: {log.entityId}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300 text-sm truncate max-w-xs">
                          {log.changes ? JSON.stringify(log.changes).slice(0, 50) + '...' : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <CheckCircle className="h-5 w-5 text-green-400 mx-auto" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-slate-400">
                Page {meta.page} of {meta.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                  disabled={page === meta.totalPages}
                  className="px-3 py-1 bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Audit Log Details</h3>
                <p className="text-sm text-slate-400">{selectedLog.entity} - {selectedLog.action}</p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Action</div>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border ${getActionConfig(selectedLog.action).color}`}>
                    {selectedLog.action}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Timestamp</div>
                  <span className="text-white text-sm">
                    {new Date(selectedLog.timestamp).toLocaleString()}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Entity</div>
                  <span className="text-white">{selectedLog.entity}</span>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Entity ID</div>
                  <span className="text-white">{selectedLog.entityId || '-'}</span>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">User</div>
                  <span className="text-white">
                    {selectedLog.user
                      ? `${selectedLog.user.firstName || ''} ${selectedLog.user.lastName || ''}`.trim() || selectedLog.user.email
                      : 'System'}
                  </span>
                  {selectedLog.userEmail && (
                    <div className="text-slate-500 text-xs">{selectedLog.userEmail}</div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">IP Address</div>
                  <span className="text-slate-400 text-sm font-mono">{selectedLog.ipAddress || '-'}</span>
                </div>
              </div>

              {/* Changes */}
              {selectedLog.changes && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Changes</div>
                  <pre className="bg-slate-900 border border-slate-700 p-3 text-xs text-slate-300 overflow-x-auto max-h-60 font-mono">
                    {JSON.stringify(selectedLog.changes, null, 2)}
                  </pre>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Metadata</div>
                  <pre className="bg-slate-900 border border-slate-700 p-3 text-xs text-slate-300 overflow-x-auto max-h-40 font-mono">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
